import { authGet, authPost, authPut, authDelete } from '../../utils/authFetch';
import { userServerAddress } from '../../common/MetaSetting';
import { fetchHttpResponseHandler, fetchHttpExceptionHandler } from '../../util/UtilFunction';

function extractArrayData(response: any, apiName: string): any[] {
  if (!response) return [];

  if (Array.isArray(response)) return response;
  if (response.data && Array.isArray(response.data)) return response.data;
  
  const possibleKeys = ['data', 'content', 'list', 'records', 'items', 'friends', 'groups', 'result'];
  for (const key of possibleKeys) {
    if (Array.isArray(response[key])) return response[key];
  }

  console.warn(`${apiName}: 无法从响应中提取数组, keys:`, Object.keys(response));
  return [];
}

export interface CreateGroupRequest {
  groupName: string;
  description?: string;
  privacy?: GroupPrivacy;
  avatarPath?: string;
  maxMembers?: number;
}

export interface UpdateGroupRequest {
  groupName?: string;
  description?: string;
  privacy?: GroupPrivacy;
  avatarPath?: string;
  maxMembers?: number;
}

export interface AddGroupMemberRequest {
  userId: number;
  role: GroupRole;
}

export interface UserGroupResponse {
  id: string;
  groupId: number;
  ownerId: number;
  groupName: string;
  description: string;
  avatarPath: string;
  members: {
    userId: number;
    role: string;
    joinedAt: string;
  }[];
  privacy: string;
  maxMembers: number;
  currentMembers: number;
  createdAt: string;
  updatedAt: string;
}

export enum GroupPrivacy {
  PUBLIC = 0,
  PRIVATE = 1,
  INVITE_ONLY = 2
}

export enum GroupRole {
  OWNER = 0,
  ADMIN = 1,
  MEMBER = 2
}

export function createGroup(request: CreateGroupRequest) {
  return authPost(`${userServerAddress}/group/create`, {
    body: JSON.stringify(request)
  })
    .then((response: any) => fetchHttpResponseHandler(response))
    .then((response) => {
      if (response?.content) {
        return response.content;
      }
      return null;
    })
    .catch(() => {
      return null;
    });
}

export function updateGroup(groupId: string, request: UpdateGroupRequest) {
  return authPut(`${userServerAddress}/group/update/${groupId}`, {
    body: JSON.stringify(request)
  })
    .then((response: any) => fetchHttpResponseHandler(response))
    .catch(fetchHttpExceptionHandler);
}

export function updateGroupAvatar(groupId: string, avatarPath: string) {
  return authPut(`${userServerAddress}/group/${groupId}/avatar?avatarPath=${encodeURIComponent(avatarPath)}`)
    .then((response: any) => fetchHttpResponseHandler(response))
    .catch(fetchHttpExceptionHandler);
}

export function deleteGroup(groupId: string) {
  return authDelete(`${userServerAddress}/group/delete/${groupId}`)
    .then((response: any) => fetchHttpResponseHandler(response))
    .catch(fetchHttpExceptionHandler);
}

export function addMember(groupId: string, request: AddGroupMemberRequest) {
  return authPost(`${userServerAddress}/group/${groupId}/members`, {
    body: JSON.stringify(request)
  })
    .then((response: any) => fetchHttpResponseHandler(response))
    .catch(fetchHttpExceptionHandler);
}

export function removeMember(groupId: string, memberUserId: string) {
  return authDelete(`${userServerAddress}/group/${groupId}/members/${memberUserId}`)
    .then((response: any) => fetchHttpResponseHandler(response))
    .catch(fetchHttpExceptionHandler);
}

export function updateMemberRole(groupId: string, memberUserId: string, role: GroupRole) {
  return authPut(`${userServerAddress}/group/${groupId}/members/${memberUserId}/role?role=${role}`)
    .then((response: any) => fetchHttpResponseHandler(response))
    .catch(fetchHttpExceptionHandler);
}

export function leaveGroup(groupId: string) {
  return authPost(`${userServerAddress}/group/${groupId}/leave`)
    .then((response: any) => fetchHttpResponseHandler(response))
    .catch(fetchHttpExceptionHandler);
}

export function getGroup(groupId: string) {
  return authGet(`${userServerAddress}/group/${groupId}`)
    .then((response: any) => fetchHttpResponseHandler(response))
    .then((response) => {
      if (response?.content) {
        return response.content;
      }
      return null;
    })
    .catch(() => {
      return null;
    });
}

export function getMyGroups() {
  return authGet(`${userServerAddress}/group/my`)
    .then((response: any) => fetchHttpResponseHandler(response))
    .then((response) => {
      const data = extractArrayData(response.content, 'getMyGroups');
      console.log('getMyGroups - extracted data length:', data.length);
      return data;
    })
    .catch((error) => {
      console.error('getMyGroups error:', error);
      return [];
    });
}

export function searchGroups(keyword: string) {
  return authGet(`${userServerAddress}/group/search?keyword=${encodeURIComponent(keyword)}`)
    .then((response: any) => fetchHttpResponseHandler(response))
    .then((response) => {
      const data = extractArrayData(response.content, 'searchGroups');
      return data;
    })
    .catch((error) => {
      console.error('searchGroups error:', error);
      return [];
    });
}

export function fetchUserGroups() {
  return getMyGroups();
}

export function fetchUserGroup(groupId: string) {
  return getGroup(groupId);
}

export function remoteUserGroupSync(request: any) {
  return createGroup(request);
}

const userGroupKeysBase = {
  all: ['userGroups'] as const,
};

export const userGroupKeys = {
  ...userGroupKeysBase,
  lists: () => [...userGroupKeysBase.all, 'list'] as const,
  list: () => [...userGroupKeysBase.all, 'list'] as const,
  details: () => [...userGroupKeysBase.all, 'detail'] as const,
  detail: (userGroupId: string) => [...userGroupKeysBase.all, 'detail', userGroupId] as const,
};
